import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../App';

// Theme configurations - Using actual background images from Keynote template
const THEMES = {
  blue: {
    name: 'Blue',
    backgroundImage: '/teesheet-assets/background-blue.jpg',
    background: 'linear-gradient(135deg, #0B132B 0%, #0d1a30 20%, #1a3a5c 40%, #2563eb 70%, #1e40af 85%, #0d2847 100%)',
    headerBg: 'rgba(60, 60, 60, 0.85)',
    rowBg: '#FFD700',
    rowAltBg: '#FFDB58',
    textColor: '#1e293b',
    titleColor: '#ffffff',
    subtitleColor: '#ffffff'
  },
  gold: {
    name: 'Sunday Gold',
    backgroundImage: '/teesheet-assets/background-gold.jpg',
    background: 'linear-gradient(135deg, #1a1500 0%, #2d2200 15%, #4a3800 30%, #d4a017 50%, #ffd700 65%, #d4a017 80%, #4a3800 95%, #1a1500 100%)',
    headerBg: 'rgba(60, 60, 60, 0.85)',
    rowBg: '#FFD700',
    rowAltBg: '#FFDB58',
    textColor: '#1e293b',
    titleColor: '#ffffff',
    subtitleColor: '#ffffff'
  },
  red: {
    name: 'Thursday Red',
    backgroundImage: '/teesheet-assets/background-red.jpg',
    background: 'linear-gradient(135deg, #0f0505 0%, #1a0808 20%, #3d1010 40%, #dc2626 70%, #991b1b 85%, #2a0a0a 100%)',
    headerBg: 'rgba(60, 60, 60, 0.85)',
    rowBg: '#FFD700',
    rowAltBg: '#FFDB58',
    textColor: '#1e293b',
    titleColor: '#ffffff',
    subtitleColor: '#ffffff'
  },
  green: {
    name: 'Green',
    backgroundImage: '/teesheet-assets/background-green.jpg',
    background: 'linear-gradient(135deg, #0a1f0a 0%, #0d2a10 20%, #1a4020 40%, #22c55e 70%, #15803d 85%, #0a2a10 100%)',
    headerBg: 'rgba(60, 60, 60, 0.85)',
    rowBg: '#FFD700',
    rowAltBg: '#FFDB58',
    textColor: '#1e293b',
    titleColor: '#ffffff',
    subtitleColor: '#ffffff'
  }
};

// Pre-configured sponsor logos from the Keynote template
const SPONSOR_LOGOS = {
  duckhook: '/teesheet-assets/duckhook-logo.png',
  duckhookMascot: '/teesheet-assets/duckhook-mascot.png',
  srixon: '/teesheet-assets/srixon-logo.png',
  mhala: '/teesheet-assets/mhala-logo.png',
  scoreCapture: '/teesheet-assets/score-capture-logo.png',
  golfConcepts: '/teesheet-assets/golf-concepts-logo.png'
};

// The Duckhook mascot is always shown in top-left
const DUCKHOOK_MASCOT = '/teesheet-assets/duckhook-mascot.png';

const PresentationTeeSheet = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const printRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [course, setCourse] = useState(null);
  const [teeTimeslots, setTeeTimeslots] = useState({});
  const [bookings, setBookings] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // Template settings
  const [settings, setSettings] = useState({
    theme: 'red', // Default to Thursday Red
    eventNumber: 1,
    totalEvents: 11,
    venue: '', // Course/venue name to display
    showDHG: true,
    showSAGA: true,
    showMascot: true, // Show the Duckhook mascot
    // Use pre-configured sponsor logos by default
    showSrixonLogo: true,
    showMhalaLogo: true,
    showScoreCaptureLogo: true,
    showGolfConceptsLogo: true
  });

  useEffect(() => {
    if (eventId) {
      fetchEventData();
    }
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      setLoading(true);
      
      const [eventResponse, teeTimesResponse] = await Promise.all([
        axios.get(`/events/${eventId}`),
        axios.get(`/events/${eventId}/tee-times`)
      ]);
      
      const eventData = eventResponse.data;
      setEvent(eventData);
      
      // Parse event number from name (e.g., "March - Sunday League#2 at Ebotse" -> 2)
      const eventNumMatch = eventData.name.match(/#(\d+)/);
      if (eventNumMatch) {
        setSettings(prev => ({
          ...prev,
          eventNumber: parseInt(eventNumMatch[1])
        }));
      }
      
      // Group tee times by tee name
      const groupedTimes = groupTeeTimesByTee(teeTimesResponse.data);
      setTeeTimeslots(groupedTimes);
      
      // Fetch bookings
      await fetchBookingsForEvent();
      
      // Fetch course if linked
      if (eventData.course_id) {
        const courseResponse = await axios.get(`/courses/${eventData.course_id}`);
        setCourse(courseResponse.data);
      }
      
    } catch (error) {
      console.error('Error fetching event data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load event information"
      });
    } finally {
      setLoading(false);
    }
  };

  // Parse event name to extract league title
  // Input: "March - Sunday League#1 at Ebotse" 
  // Output: "2026 SUNDAY LEAGUE#1"
  const parseEventTitle = (eventName) => {
    if (!eventName) return '';
    
    // Get year from event date or current year
    const year = event?.date ? new Date(event.date).getFullYear() : new Date().getFullYear();
    
    // Try to extract league name pattern (e.g., "Sunday League#1", "Thursday League#5")
    // Pattern: Look for "[Day] League#[Number]" or similar
    const leagueMatch = eventName.match(/(\w+\s*League\s*#?\d*)/i);
    
    if (leagueMatch) {
      // Found a league pattern, format it with year
      let leagueName = leagueMatch[1].toUpperCase();
      // Ensure # is present before number
      leagueName = leagueName.replace(/LEAGUE\s*(\d)/, 'LEAGUE#$1');
      return `${year} ${leagueName}`;
    }
    
    // Fallback: Remove month prefix and "at [Venue]" suffix, then uppercase
    let title = eventName;
    
    // Remove month prefix (e.g., "March - ", "January - ")
    title = title.replace(/^(January|February|March|April|May|June|July|August|September|October|November|December)\s*[-–—]\s*/i, '');
    
    // Remove "at [Venue]" suffix
    title = title.replace(/\s+at\s+.+$/i, '');
    
    return `${year} ${title.toUpperCase()}`;
  };

  const fetchBookingsForEvent = async () => {
    try {
      const response = await axios.get(`/events/${eventId}/bookings`);
      const eventBookings = response.data;
      
      // Transform bookings into organized structure by slot
      const bookingsData = {};
      eventBookings.forEach(booking => {
        // Normalize tee_time to just time portion (handle both ISO datetime and simple time)
        let teeTimeKey = booking.tee_time;
        if (teeTimeKey && teeTimeKey.includes('T')) {
          teeTimeKey = teeTimeKey.split('T')[1].substring(0, 5); // Get HH:MM
        } else if (teeTimeKey) {
          teeTimeKey = teeTimeKey.substring(0, 5); // Ensure HH:MM format
        }
        
        const teeName = booking.tee_name || '1st';
        const slotIndex = booking.slot_index || 0;
        const slotKey = `${teeName}_${teeTimeKey}_${slotIndex}`;
        
        if (!bookingsData[slotKey]) {
          bookingsData[slotKey] = [];
        }
        
        bookingsData[slotKey].push({
          id: booking.id,
          user_id: booking.user_id,
          user_name: booking.user_name || '',
          is_guest: booking.is_guest || false,
          guest_name: booking.guest_name || '',
          is_reserved: booking.is_reserved || false,
          hna_handicap: booking.hna_handicap,
          dh_handicap: booking.dh_handicap,
          is_new_player: booking.is_new_player || false,
          has_hna: booking.has_hna !== false
        });
      });
      
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const groupTeeTimesByTee = (teeTimes) => {
    const grouped = {};
    teeTimes.forEach(slot => {
      const teeName = slot.tee_name || '1st';
      if (!grouped[teeName]) {
        grouped[teeName] = [];
      }
      grouped[teeName].push(slot);
    });
    
    // Sort each group by time
    Object.keys(grouped).forEach(tee => {
      grouped[tee].sort((a, b) => a.time.localeCompare(b.time));
    });
    
    return grouped;
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix = getDaySuffix(day);
    const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
    return `${day}${suffix} ${month}`;
  };

  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return 'TH';
    switch (day % 10) {
      case 1: return 'ST';
      case 2: return 'ND';
      case 3: return 'RD';
      default: return 'TH';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Handle ISO datetime format (2026-01-09T06:00:00) or simple time format (06:00)
    if (timeString.includes('T')) {
      const timePart = timeString.split('T')[1];
      const [hours, minutes] = timePart.split(':');
      return `${hours}:${minutes}`;
    }
    const [hours, minutes] = timeString.split(':');
    return `${hours}:${minutes}`;
  };

  const getPlayerName = (booking) => {
    if (!booking) return 'AVAILABLE';
    if (booking.is_reserved) return 'RESERVED';
    if (booking.is_guest) return booking.guest_name?.toUpperCase() || 'GUEST';
    return booking.user_name?.toUpperCase() || 'UNKNOWN';
  };

  const getHandicapDisplay = (booking) => {
    if (!booking || booking.is_reserved) {
      return { dhg: '', saga: '' };
    }
    
    const dhg = booking.dh_handicap !== null && booking.dh_handicap !== undefined 
      ? booking.dh_handicap 
      : '';
    const saga = booking.hna_handicap !== null && booking.hna_handicap !== undefined 
      ? booking.hna_handicap 
      : 'NA';
    
    return { dhg, saga };
  };

  const handleExportPNG = async () => {
    if (!printRef.current) return;
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `tee-sheet-${event?.name || eventId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast({
        title: "Export Complete",
        description: "Tee sheet exported as PNG"
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: "Failed to export tee sheet"
      });
    }
  };

  const currentTheme = THEMES[settings.theme] || THEMES.blue;
  const teeNames = Object.keys(teeTimeslots);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading tee sheet...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center text-white">
          <p>Event not found</p>
          <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Control Panel - Hidden when printing */}
      <div className="print:hidden bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate(-1)} className="text-gray-300 border-gray-600">
                ← Back
              </Button>
              <h1 className="text-lg font-bold text-white">Presentation Tee Sheet</h1>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Theme Selector */}
              <div className="flex items-center gap-2">
                <Label className="text-gray-300 text-sm">Theme:</Label>
                <Select
                  value={settings.theme}
                  onValueChange={(value) => setSettings({...settings, theme: value})}
                >
                  <SelectTrigger className="w-36 bg-gray-700 border-gray-600 text-white" data-testid="theme-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blue">Sunday Blue</SelectItem>
                    <SelectItem value="gold">Sunday Gold</SelectItem>
                    <SelectItem value="red">Thursday Red</SelectItem>
                    <SelectItem value="capeTown">Cape Town</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Settings Button */}
              <Button 
                variant="outline" 
                onClick={() => setSettingsOpen(true)}
                className="text-gray-300 border-gray-600"
                data-testid="settings-btn"
              >
                ⚙️ Settings
              </Button>
              
              {/* Export Button */}
              <Button 
                onClick={handleExportPNG}
                className="bg-green-600 hover:bg-green-700"
                data-testid="export-png-btn"
              >
                📥 Export PNG
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tee Sheet Canvas */}
      <div className="container mx-auto p-4 print:p-0">
        <div 
          ref={printRef}
          className="relative overflow-hidden rounded-lg print:rounded-none"
          style={{
            backgroundImage: currentTheme.backgroundImage ? `url(${currentTheme.backgroundImage})` : 'none',
            backgroundColor: '#0B132B',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '800px',
            paddingBottom: '120px'
          }}
        >
          {/* Fallback gradient overlay if image doesn't load */}
          {!currentTheme.backgroundImage && (
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{ background: currentTheme.background }}
            />
          )}

          {/* Duckhook Mascot - Top left corner */}
          {settings.showMascot && (
            <div className="absolute top-2 left-4 z-20">
              <img 
                src={DUCKHOOK_MASCOT} 
                alt="Duckhook Golfers" 
                className="h-32 w-auto"
                style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.5))' }}
              />
            </div>
          )}

          {/* Powered By SRIXON - Top right area */}
          {settings.showSrixonLogo && (
            <div 
              className="absolute top-16 right-8 flex items-center gap-3 z-20"
            >
              <span 
                className="text-white text-xl"
                style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
              >
                Powered by
              </span>
              <img src={SPONSOR_LOGOS.srixon} alt="SRIXON" className="h-8" />
            </div>
          )}

          {/* Header Section */}
          <div className="relative pt-4 pb-2 px-6">
            {/* Event Title - White/Silver 3D metallic embossed effect */}
            <h1 
              className="text-center text-5xl md:text-6xl font-black tracking-wide mb-3 uppercase"
              style={{ 
                color: '#e8e8e8',
                textShadow: `
                  1px 1px 0 #ffffff,
                  2px 2px 0 #d0d0d0,
                  3px 3px 0 #b0b0b0,
                  4px 4px 0 #909090,
                  5px 5px 0 #707070,
                  6px 6px 0 #505050,
                  7px 7px 0 #303030,
                  8px 8px 12px rgba(0,0,0,0.8)
                `,
                fontFamily: '"Impact", "Arial Black", sans-serif',
                letterSpacing: '0.08em',
                WebkitTextStroke: '0.5px #888888'
              }}
            >
              {parseEventTitle(event.name)}
            </h1>
            
            {/* Date and Course on dark gray bar */}
            <div className="flex justify-center mb-2">
              <div 
                className="px-16 py-3"
                style={{
                  background: 'rgba(80, 80, 80, 0.9)',
                }}
              >
                <p 
                  className="text-2xl text-center uppercase"
                  style={{ 
                    color: '#ffffff',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontStyle: 'italic',
                    letterSpacing: '0.1em'
                  }}
                >
                  {formatEventDate(event.date)}
                </p>
                <p 
                  className="text-2xl text-center uppercase mt-1"
                  style={{ 
                    color: '#ffffff',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontStyle: 'italic',
                    letterSpacing: '0.1em'
                  }}
                >
                  {settings.venue || course?.name?.toUpperCase() || ''}
                </p>
              </div>
            </div>

            {/* Event Counter - Bradley Hand Bold */}
            <p 
              className="text-xl mt-2"
              style={{ 
                color: '#ffffff',
                fontFamily: '"Bradley Hand", "Bradley Hand ITC", "Segoe Script", cursive',
                fontWeight: 'bold',
                fontStyle: 'italic'
              }}
            >
              Event {settings.eventNumber} of {settings.totalEvents}
            </p>
          </div>

          {/* Tee Sheets */}
          <div className="relative px-6 pb-4 space-y-6">
            {teeNames.map(teeName => (
              <div key={teeName}>
                {/* Table */}
                <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <th 
                        className="p-2 text-white text-left font-bold text-lg border border-gray-600"
                        style={{ 
                          backgroundColor: currentTheme.headerBg,
                          width: '12%'
                        }}
                      >
                        {teeName} Tee
                      </th>
                      <th 
                        className="p-2 text-white text-center font-bold text-lg border border-gray-600"
                        style={{ backgroundColor: currentTheme.headerBg, width: '22%' }}
                      >
                        PLAYER 1
                      </th>
                      <th 
                        className="p-2 text-white text-center font-bold text-lg border border-gray-600"
                        style={{ backgroundColor: currentTheme.headerBg, width: '22%' }}
                      >
                        PLAYER 2
                      </th>
                      <th 
                        className="p-2 text-white text-center font-bold text-lg border border-gray-600"
                        style={{ backgroundColor: currentTheme.headerBg, width: '22%' }}
                      >
                        PLAYER 3
                      </th>
                      <th 
                        className="p-2 text-white text-center font-bold text-lg border border-gray-600"
                        style={{ backgroundColor: currentTheme.headerBg, width: '22%' }}
                      >
                        PLAYER 4
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(teeTimeslots[teeName] || []).map((teeTime, timeIndex) => {
                      // Normalize tee time for lookup
                      let normalizedTime = teeTime.time;
                      if (normalizedTime && normalizedTime.includes('T')) {
                        normalizedTime = normalizedTime.split('T')[1].substring(0, 5);
                      } else if (normalizedTime) {
                        normalizedTime = normalizedTime.substring(0, 5);
                      }
                      
                      // Get bookings for all 4 slots at this tee time
                      const slots = [0, 1, 2, 3].map(slotIndex => {
                        const slotKey = `${teeName}_${normalizedTime}_${slotIndex}`;
                        return bookings[slotKey]?.[0] || null;
                      });
                      
                      return (
                        <tr key={`${teeName}_${teeTime.time}`}>
                          {/* Time Column */}
                          <td 
                            className="p-2 text-center font-bold border border-gray-600"
                            style={{ 
                              backgroundColor: currentTheme.rowBg,
                              color: currentTheme.textColor
                            }}
                          >
                            {formatTime(teeTime.time)}
                          </td>
                          
                          {/* Player Columns */}
                          {slots.map((booking, idx) => {
                            const handicap = getHandicapDisplay(booking);
                            const isAvailable = !booking;
                            
                            return (
                              <td 
                                key={idx}
                                className="p-1 text-center border border-gray-600"
                                style={{ 
                                  backgroundColor: currentTheme.rowBg,
                                  color: currentTheme.textColor
                                }}
                              >
                                <div className="font-bold text-sm truncate">
                                  {getPlayerName(booking)}
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {settings.showDHG && settings.showSAGA ? (
                                    <>DHG: {handicap.dhg} / SAGA: {handicap.saga}</>
                                  ) : settings.showDHG ? (
                                    <>DHG: {handicap.dhg}</>
                                  ) : settings.showSAGA ? (
                                    <>SAGA: {handicap.saga}</>
                                  ) : null}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Sponsor Bar - Logos FILL entire strip top to bottom */}
          <div 
            className="absolute bottom-0 left-0 right-0 flex items-stretch justify-between"
            style={{ 
              backgroundColor: 'rgba(50, 50, 50, 0.6)',
              height: '120px'
            }}
          >
            {/* Score Capture Logo - ENLARGED */}
            {settings.showScoreCaptureLogo && (
              <div className="flex-1 flex items-center justify-center" style={{ transform: 'scale(1.8)' }}>
                <img src={SPONSOR_LOGOS.scoreCapture} alt="Score Capture" className="h-full object-contain py-1" style={{ maxHeight: '118px' }} />
              </div>
            )}
            
            {/* Srixon Logo - Perfect size */}
            {settings.showSrixonLogo && (
              <div className="flex-1 flex items-center justify-center">
                <img src={SPONSOR_LOGOS.srixon} alt="Srixon" className="h-full object-contain py-1" style={{ maxHeight: '118px' }} />
              </div>
            )}
            
            {/* Mhala Tours Logo - Perfect size */}
            {settings.showMhalaLogo && (
              <div className="flex-1 flex items-center justify-center">
                <img src={SPONSOR_LOGOS.mhala} alt="Mhala Tours" className="h-full object-contain py-1" style={{ maxHeight: '118px' }} />
              </div>
            )}
            
            {/* Golf Concepts Logo - ENLARGED */}
            {settings.showGolfConceptsLogo && (
              <div className="flex-1 flex items-center justify-center" style={{ transform: 'scale(1.8)' }}>
                <img src={SPONSOR_LOGOS.golfConcepts} alt="Golf Concepts" className="h-full object-contain py-1" style={{ maxHeight: '118px' }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md bg-gray-800 text-white border-gray-700">
          <DialogHeader>
            <DialogTitle>Tee Sheet Settings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Event Number */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Event Number</Label>
                <Input
                  type="number"
                  value={settings.eventNumber}
                  onChange={(e) => setSettings({...settings, eventNumber: parseInt(e.target.value) || 1})}
                  className="bg-gray-700 border-gray-600 text-white"
                  min="1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Total Events</Label>
                <Input
                  type="number"
                  value={settings.totalEvents}
                  onChange={(e) => setSettings({...settings, totalEvents: parseInt(e.target.value) || 11})}
                  className="bg-gray-700 border-gray-600 text-white"
                  min="1"
                />
              </div>
            </div>
            
            {/* Venue/Course Name */}
            <div>
              <Label className="text-gray-300">Venue / Course Name</Label>
              <Input
                value={settings.venue}
                onChange={(e) => setSettings({...settings, venue: e.target.value})}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Enter course name (e.g., SILVER LAKES)"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to use the event's assigned course</p>
            </div>
            
            {/* Handicap Display */}
            <div>
              <Label className="text-gray-300 mb-2 block">Show Handicaps</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={settings.showDHG}
                    onChange={(e) => setSettings({...settings, showDHG: e.target.checked})}
                    className="rounded"
                  />
                  DHG
                </label>
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={settings.showSAGA}
                    onChange={(e) => setSettings({...settings, showSAGA: e.target.checked})}
                    className="rounded"
                  />
                  SAGA
                </label>
              </div>
            </div>
            
            {/* Show Mascot Toggle */}
            <div>
              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={settings.showMascot}
                  onChange={(e) => setSettings({...settings, showMascot: e.target.checked})}
                  className="rounded"
                />
                Show Duckhook Mascot
              </label>
            </div>
            
            {/* Sponsor Logo Toggles */}
            <div>
              <Label className="text-gray-300 mb-2 block">Sponsor Logos</Label>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-center gap-2 text-gray-300 text-sm">
                  <input
                    type="checkbox"
                    checked={settings.showSrixonLogo}
                    onChange={(e) => setSettings({...settings, showSrixonLogo: e.target.checked})}
                    className="rounded"
                  />
                  Srixon
                </label>
                <label className="flex items-center gap-2 text-gray-300 text-sm">
                  <input
                    type="checkbox"
                    checked={settings.showMhalaLogo}
                    onChange={(e) => setSettings({...settings, showMhalaLogo: e.target.checked})}
                    className="rounded"
                  />
                  Mhala Tours
                </label>
                <label className="flex items-center gap-2 text-gray-300 text-sm">
                  <input
                    type="checkbox"
                    checked={settings.showScoreCaptureLogo}
                    onChange={(e) => setSettings({...settings, showScoreCaptureLogo: e.target.checked})}
                    className="rounded"
                  />
                  Score Capture
                </label>
                <label className="flex items-center gap-2 text-gray-300 text-sm">
                  <input
                    type="checkbox"
                    checked={settings.showGolfConceptsLogo}
                    onChange={(e) => setSettings({...settings, showGolfConceptsLogo: e.target.checked})}
                    className="rounded"
                  />
                  Golf Concepts
                </label>
              </div>
            </div>
            
            <Button 
              onClick={() => setSettingsOpen(false)}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PresentationTeeSheet;
